import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { quotaGateFromStatus, type QuotaGateState } from '../lib/quota'
import { useAuthStore } from '../stores/authStore'
import QuotaGate from './QuotaGate'

interface QuotaResponse {
  cap_usd: number
  spent_usd: number
  remaining_usd: number
  exceeded: boolean
}

/**
 * Inline banner shown when a managed-key user has exceeded their monthly quota.
 * BYO-key users are exempt and never see this banner.
 * Fetches quota status once on mount; does not poll.
 *
 * Renders the shared <QuotaGate> (mapped via lib/quota) so the proactive banner
 * carries the same CTAs as the synchronous-flow and onboarding gates.
 */
export default function QuotaBanner() {
  const user = useAuthStore((s) => s.user)
  const [gate, setGate] = useState<QuotaGateState | null>(null)

  useEffect(() => {
    // BYO users are exempt from quota
    if (!user || user.apiKeyMode === 'byo') return

    let cancelled = false
    api
      .get<QuotaResponse>('/api/v1/user/quota')
      .then((data) => {
        if (!cancelled) setGate(quotaGateFromStatus(data))
      })
      .catch(() => {
        // Silently ignore — banner simply won't show
      })
    return () => {
      cancelled = true
    }
  }, [user])

  if (!gate) return null

  return <QuotaGate gate={gate} variant="banner" />
}
